import React from 'react';
import { useTranslation } from 'react-i18next';
import _isNil from 'lodash/isNil';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import PercentageBarCell from '@lib/components/Table/commonComponents/PercentageBarCell/PercentageBarCell';
import Typography from '@lib/components/Typography/Typography';
import formatCount from '@lib/utils/formatCount';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import PlanInfoTooltipIcon from './PlanInfoTooltipIcon';
import styles from './CompanyPlanView.module.scss';

export interface AiUsageSectionProps {
  usedCredits?: number | null;
  totalCredits?: number | null;
  // The company's whole AI budget consumption. The two below split it by who
  // spent it, which is why they carry an explanation each.
  totalUsagePercentage?: number | null;
  userUsagePercentage?: number | null;
  systemUsagePercentage?: number | null;
}

/** AI credits consumed, and how the budget splits between user and system work. */
export default function AiUsageSection({
  usedCredits,
  totalCredits,
  totalUsagePercentage,
  userUsagePercentage,
  systemUsagePercentage,
}: AiUsageSectionProps) {
  const { t, i18n } = useTranslation();

  return (
    <>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="toll" symbolsOutlined />
          <Typography variant="caption">{t('ai-credits')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            {_isNil(usedCredits) || _isNil(totalCredits)
              ? '-'
              : `${formatCount(usedCredits, i18n.language)} / ${formatCount(totalCredits, i18n.language)}`}
          </Typography>
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="auto_awesome" symbolsOutlined />
          <Typography variant="caption">{t('total-usage')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <PercentageBarCell percentage={totalUsagePercentage} precision={2} />
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="auto_awesome" symbolsOutlined />
          <Typography variant="caption">{t('user-usage')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <div className={styles.usageRow}>
            <PercentageBarCell percentage={userUsagePercentage} precision={2} />
            <PlanInfoTooltipIcon body={t('user-usage-description')} />
          </div>
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="auto_awesome" symbolsOutlined />
          <Typography variant="caption">{t('system-usage')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <div className={styles.usageRow}>
            <PercentageBarCell
              percentage={systemUsagePercentage}
              precision={2}
            />
            <PlanInfoTooltipIcon body={t('system-usage-description')} />
          </div>
        </div>
      </div>
    </>
  );
}
